import React, {useState, useEffect, useRef} from "react";
import {Link, useLocation} from "react-router-dom";
import { Overlay, Tooltip } from "react-bootstrap";

import SimpleModal from "./ui/simple-modal";

import Unity, { UnityContext } from "react-unity-webgl";

import UsersDataService from "../services/users";
import ScoresDataService from "../services/scores";

const unityContext = new UnityContext({
  loaderUrl: process.env.REACT_APP_API_BASE_URL + "/slizer_webgl/slizer_webgl.loader.js",
  dataUrl: process.env.REACT_APP_API_BASE_URL + "/slizer_webgl/slizer_webgl.data.gz",
  frameworkUrl: process.env.REACT_APP_API_BASE_URL + "/slizer_webgl/slizer_webgl.framework.js.gz",
  codeUrl: process.env.REACT_APP_API_BASE_URL + "/slizer_webgl/slizer_webgl.wasm.gz",
  webGLContextAttributes: {
    powerPreference: "high-performance",
    xrCompatible: true,
  },
});

const Slizer = props => {
  const [users, setUsers] = useState([]);
  const [username, setUsername] = useState("");

  const [loadProgress, setLoadProgression] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const[expanded, setExpanded] = useState(false);

  const [showAlert, setShowAlert] = useState(false);
  const [showUsernameTooltip, setShowUsernameTooltip] = useState(false);

  let location = useLocation();

  const usernameInput = React.createRef();
  const gameWindow = React.createRef();

  // This is here only so that the Unity thingy can actually unload or memory goes poo poo
  window.addEventListener("beforeunload", function(event) { 
    event.preventDefault();
    event.returnValue = "Is Unity unloaded?";
    unityContext.quitUnityInstance();
   });

  useEffect(() => {
    retrieveTopUsers();

    unityContext.on("progress", function (progression) {
      setLoadProgression(progression);
    });

  }, [])

  unityContext.on("loaded", function () {
    setLoaded(true);
    gameWindow.current.scrollIntoView();

    if((window.innerHeight / window.innerWidth) >= 1)
      {
        setShowAlert(true);
      }
  });

  unityContext.on("hasUsername", function (username) {
    usernameInput.current.value = username;
    setUsername(username);
  });

  unityContext.on("submitScore", function (score) {
    if(username != "")
      ScoresDataService.addScore(username, score, new Date())
  });

  const retrieveTopUsers = () => {
    UsersDataService.getTopThree().then(response => {
      console.log(response.data);
      setUsers(response.data.users);
    }).catch(e => {
      console.error(`Error retrieving users: ${e}`);
    })
  };

  const onChangeUsername = (e) =>
  {
    // For some weird reason if this gets called before unityInstance finishes loading gameWindow becomes null...
    // so we have to diasble the whole input element when loaded == false
    if(e.target.checkValidity()){
      e.target.classList.add("is-valid");
      e.target.classList.remove("is-invalid");
      setShowUsernameTooltip(false);
    }
    else{
      //e.target.tooltip({'title':"Invalid"});
      if(e.target.value != ""){
        e.target.classList.remove("is-valid");
        e.target.classList.add("is-invalid");
        setShowUsernameTooltip(true);
      }
      else{
        e.target.classList.remove("is-valid");
        e.target.classList.remove("is-invalid");
        setShowUsernameTooltip(false);
      }
    }
  }

  const onConfirmUsername = () => {
    const username = usernameInput.current.value;
    // Add user to db if not exists
    UsersDataService.createUser({"username": username}).then(result => {
      setUsername(username);
      unityContext.send("WebManager", "SetUsername", username);
    }).catch(error => {
      console.log(error);
    });
  }

  // Trick to make useEffect only run on update, not intial mount
  const isInitialMount = useRef(true);

  // We can use multiple useEffects (THIS CHANGES EVERYTHING)
  // Use this to scroll to the game window after expanding/minimizing
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
   } else {
      gameWindow.current.scrollIntoView({inline: "center"});
   }
  }, [expanded]);

  return (
    <div style={location.pathname != "/" ? {display: "none"} : {}}>
      <SimpleModal show={showAlert} onClose={() => setShowAlert(false)} body="This game is designed to be played in landscape mode. Please rotate your device to get the best experience!" primaryBtn="OK" />
      {/* Leaderboard */}
      <div style={{textAlign: "center"}}>
        <Link to = {"/leaderboard"}><h3>Leaderboard</h3></Link>
        <ul className="list-group" style={{width: 200, display:"inline-block"}}>
          {users.map((user, index) => {
            const username = `${user.username}`;
            const highscore = `${user.highscore}`
            const rank_color = index == 0 ? "gold" : index == 1 ? "silver" : index == 2 ? "rgb( 205, 127, 50 )" : "";
            return (
              <div className = "leaderboard_entry" key = {user._id}>
                <Link to = {`/user/${user.username}`}><li className="list-group-item" style = {{ backgroundColor: rank_color}}>{username} - {highscore}</li></Link>
              </div>
            )
          })}
        </ul>
      </div>
      <br/>
      {/* Name input */}
      <div style={{textAlign: "center", maxWidth:500, margin: "auto"}}>
        <div className="input-group col-lg-4">
          <input id = "yomom" onChange={onChangeUsername} required pattern="[a-zA-Z0-9]{1,20}" ref = {usernameInput} type="text" className="form-control" placeholder="Enter your name to save your stats and be ranked" disabled = {(loaded && username == "") ? false : true} title="Username can only contain alphabet letters and numbers. Maximum length: 20 characters."/>
          <Overlay target={usernameInput} show={showUsernameTooltip} placement="right">
            {(props) => (
              <Tooltip id="tooltip-username-error" {...props}>
                Username can only contain alphabet letters and numbers. Maximum length: 20 characters.
              </Tooltip>
            )}
          </Overlay>
          <div className="input-group-append">
            <button onClick={onConfirmUsername} className="btn btn-outline-secondary" type = "button" disabled = {username == "" ? false : true} hidden = {!loaded ? "none" : ""}>
              Confirm
            </button>
          </div>
        </div>
      </div>

      {/* Actual game */}
      <div style = {{display: "flex", textAlign: "center", justifyContent: "center", position: "relative", width: expanded ? "100vw" : "100%", height: expanded ? "100vh" : "", paddingTop: "56.25%"}}>
        <div ref={gameWindow} style={{marginLeft: "auto", marginRight: "auto", position: "absolute", top: 0, left: 0, bottom: 0, right: 0}}>
          <Unity unityContext={unityContext} tabIndex = {1}
          style = {{
          height: "100%",
          width: "100%",
          background: "grey",
          }} />
          {loaded &&
            // Object.assign helps combine multiple {} together, very epic
            <button alt = "Minimize/Maximize" onClick={(e) => {setExpanded(!expanded);}} className="btn btn-outline-primary" type = "button" style = {Object.assign({position: "absolute", opacity: 0.3}, expanded ? {right: 10, bottom: 150, width: 80, height: 80} : {right: 2, bottom: 120, width: 60, height: 60})}>
            {!expanded ?   
            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 250 250" style = {{ width: "100%", height: "auto" }} xmlSpace="preserve">
              <path style = {{fill: "white"}} id="XMLID_15_" d="M227.133,83.033c8.283,0,15-6.716,15-15V15c0-8.284-6.717-15-15-15H174.1c-8.284,0-15,6.716-15,15 s6.716,15,15,15h16.82l-69.854,69.854L51.213,30h16.82c8.284,0,15-6.716,15-15s-6.716-15-15-15H15C6.717,0,0,6.716,0,15v53.033 c0,8.284,6.717,15,15,15c8.285,0,15-6.716,15-15v-16.82l69.854,69.854L30,190.92V174.1c0-8.284-6.715-15-15-15 c-8.283,0-15,6.716-15,15v53.033c0,8.284,6.717,15,15,15h53.033c8.284,0,15-6.716,15-15c0-8.284-6.716-15-15-15h-16.82 l69.854-69.854l69.854,69.854H174.1c-8.284,0-15,6.716-15,15c0,8.284,6.716,15,15,15h53.033c8.283,0,15-6.716,15-15V174.1 c0-8.284-6.717-15-15-15c-8.285,0-15,6.716-15,15v16.82l-69.854-69.854l69.854-69.854v16.82 C212.133,76.317,218.848,83.033,227.133,83.033z"/>
            </svg>
            :
            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 55 55" style = {{ width: "100%", height: "auto" }} xmlSpace="preserve">
              <path style = {{fill: "white"}} d="M18.246,5.023c-1.657,0-3,1.343-3,3v4.072L5.121,1.971C3.95,0.8,2.05,0.8,0.878,1.971c-1.171,1.171-1.171,3.071,0,4.243 l9.882,9.881H7.173c-1.657,0-3,1.343-3,3s1.343,3,3,3h10.83c0.197,0,0.394-0.021,0.587-0.059c0.088-0.018,0.169-0.051,0.254-0.076 c0.103-0.03,0.207-0.054,0.306-0.095c0.1-0.042,0.189-0.098,0.283-0.149c0.078-0.042,0.159-0.077,0.233-0.126 c0.162-0.108,0.312-0.23,0.449-0.367c0.002-0.003,0.006-0.004,0.008-0.007c0.006-0.006,0.009-0.013,0.015-0.019 c0.034-0.035,0.061-0.077,0.094-0.114c0.616-0.55,1.011-1.341,1.011-2.231V8.022C21.246,6.367,19.903,5.023,18.246,5.023z"/><path style = {{fill: "white"}} d="M20.235,34.257c-0.17-0.19-0.355-0.365-0.568-0.507c-0.072-0.048-0.149-0.081-0.224-0.122 c-0.097-0.053-0.189-0.111-0.292-0.153c-0.099-0.041-0.201-0.064-0.302-0.094c-0.086-0.025-0.168-0.059-0.258-0.076 c-0.194-0.039-0.391-0.06-0.587-0.06H7.174c-1.657,0-3,1.343-3,3s1.343,3,3,3h3.588l-9.883,9.883c-1.172,1.171-1.172,3.071,0,4.242 C1.464,53.956,2.233,54.249,3,54.249s1.536-0.293,2.121-0.879l10.125-10.125v4.072c0,1.657,1.343,3,3,3s3-1.343,3-3v-10.83 C21.246,35.597,20.851,34.806,20.235,34.257z"/><path style = {{fill: "white"}} d="M54.463,49.128l-9.883-9.883h3.588c1.657,0,3-1.343,3-3s-1.343-3-3-3h-10.83c-0.196,0-0.394,0.021-0.587,0.06 c-0.093,0.018-0.179,0.052-0.268,0.079c-0.098,0.029-0.197,0.051-0.292,0.09c-0.108,0.045-0.207,0.106-0.309,0.163 c-0.068,0.038-0.141,0.068-0.207,0.112c-0.213,0.142-0.399,0.316-0.569,0.508c-0.615,0.549-1.011,1.34-1.011,2.23v10.83 c0,1.657,1.343,3,3,3s3-1.343,3-3v-4.072L50.221,53.37c0.586,0.586,1.354,0.879,2.121,0.879s1.535-0.293,2.121-0.879 C55.635,52.199,55.635,50.299,54.463,49.128z"/><path style = {{fill: "white"}} d="M34.986,21.086c0.169,0.19,0.355,0.365,0.568,0.507c0.068,0.046,0.142,0.077,0.213,0.116c0.1,0.056,0.196,0.115,0.303,0.16 c0.097,0.04,0.196,0.063,0.296,0.092c0.088,0.026,0.172,0.06,0.264,0.078c0.193,0.039,0.39,0.059,0.587,0.059h10.83 c1.657,0,3-1.343,3-3s-1.343-3-3-3h-3.588l9.883-9.883c1.172-1.171,1.172-3.071,0-4.242c-1.172-1.172-3.07-1.172-4.242,0 L39.975,12.098V8.026c0-1.657-1.343-3-3-3s-3,1.343-3,3v10.83C33.975,19.746,34.37,20.537,34.986,21.086z"/>
            </svg>
            }
            </button>
          }
        </div>
      </div>
      {!loaded &&
        <div className="progress">
          <div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style={{width: (loadProgress * 100) +"%"}} aria-valuenow={loadProgress * 100} aria-valuemin={0} aria-valuemax={100}>{loadProgress * 100}%</div>
        </div>
      }
    </div>
  );
  
}

export default Slizer;
